<template>
  <div>
    <load-profile />
    <n-search-container
      :fields="searchFields"
      @search="search"
      @reset="reset"
    >
      <n-search-input
        ref="search"
        v-model="params"
        :fields="searchFields"
      />
    </n-search-container>

    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col>
            <label>{{ $t('general.entries') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            >
              <template #no-options>
                {{ $t("selection.noOption") }}
              </template>
            </v-select>
          </b-col>
          <b-col
            v-if="$can('create', permissionType)"
            class="text-right"
          >
            <b-button
              variant="primary"
              @click="showModal"
            >
              {{ $t('button.create') }}
            </b-button>
          </b-col>
        </b-row>
      </div>
      <div>
        <n-table
          ref="table"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="true"
          :updatable="false"
          :type="permissionType"
          :route="route"
          @sort-changed="sortChanged"
          @destroy="destroy"
        >
          <template #cell(employee)="row">
            {{ row.item.merchantUserId.firstName }} {{ row.item.merchantUserId.lastName }}
          </template>
          <template #cell(date)="row">
            {{ row.item.date | formatDate('LL') }}<span v-if="row.item.endDate"> - {{ row.item.endDate | formatDate('LL') }}</span>
          </template>
          <template #cell(remark)="row">
            <div
              v-if="row.item.remark"
              :id="`remark-${row.index}`"
              v-b-tooltip.hover="row.item.remark"
              class="text-truncate remark-cell"
              style="max-width: 100%;"
            >
              {{ row.item.remark }}
            </div>
            <span v-else>-</span>
          </template>
          <template #front-button="row">
            <b-button
              v-b-tooltip.hover
              :title="$t('general.update')"
              variant="primary"
              pill
              size="sm"
              @click="editItem(row.item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>
    <b-modal
      v-model="isModalVisible"
      :title="modalTitle"
      hide-footer
      no-close-on-backdrop
      @hidden="resetForm"
      @ok.prevent="handleSubmit"
    >
      <validation-observer
        ref="formValidation"
        #default="{ invalid }"
      >
        <validation-provider
          v-slot="{ errors }"
          :name="$t('breadcrumb.employee')"
          rules="required"
        >
          <b-form-group
            :label="$t('breadcrumb.employee')"
            label-for="employee"
          >
            <n-async-single-select
              v-model="form.merchantUserId"
              repository="allOperator"
              :is-custom-params="true"
              :clearable="false"
              :state="errors.length > 0 ? false : null"
              selection-key="id"
              selection-label="name"
              :must-excluded-ids="[]"
              :placeholder="$t('placeholder.selectEmployee')"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          :name="$t('field.startDate')"
          rules="required"
        >
          <b-form-group
            :label="$t('field.startDate')"
            label-for="date"
          >
            <b-form-datepicker
              id="date"
              v-model="form.date"
              v-bind="labels[$i18n.locale] || {}"
              :locale="$i18n.locale"
              :max="form.endDate"
              :state="errors.length > 0 ? false : null"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          :name="$t('field.endDate')"
          rules="required"
        >
          <b-form-group
            :label="$t('field.endDate')"
            label-for="endDate"
          >
            <b-form-datepicker
              id="endDate"
              v-model="form.endDate"
              v-bind="labels[$i18n.locale] || {}"
              :locale="$i18n.locale"
              :min="form.date"
              :state="errors.length > 0 ? false : null"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          :name="$t('field.remark')"
          rules="required"
        >
          <b-form-group
            :label="$t('field.remark')"
            label-for="remark"
          >
            <b-form-textarea
              id="remark"
              v-model="form.remark"
              :placeholder="$t('placeholder.enterRemark')"
              rows="3"
              :state="errors.length > 0 ? false : null"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <b-row class="mt-3">
          <b-col class="text-right">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="isModalVisible = false"
            >
              {{ $t('button.cancel') }}
            </b-button>
            <b-button
              variant="primary"
              :disabled="
                invalid || submitting || !form.merchantUserId || !form.date || !form.endDate
              "
              @click="onSubmit"
            >
              <span
                v-if="submitting"
                class="spinner-border spinner-border-sm mr-1"
              />
              {{ isEditing ? $t('button.update') : $t('button.save') }}
            </b-button>
          </b-col>
        </b-row>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BModal,
  BForm,
  BFormGroup,
  BFormDatepicker,
  BFormTextarea,
  BFormInvalidFeedback,
} from 'bootstrap-vue'
import NAsyncSingleSelect from '@/components/NAsyncSingleSelect.vue'
import Repository from '@/repositories/RepositoryFactory'
import NPagination from '@/components/NPagination'
import NTable from '@/components/NTable'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import NSearchContainer from '@/components/NSearchContainer'
import NSearchInput from '@/components/NSearchInput'
import VSelect from 'vue-select'
import SearchInputs from './searchInput'
import tableField from './tableField'
import labels from '@/data/localeDatepicker';

const DayOffRepository = Repository.get('dayOff')

export default {
  name: 'DayOffList',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BModal,
    BForm,
    BFormGroup,
    BFormDatepicker,
    BFormTextarea,
    NAsyncSingleSelect,
    NPagination,
    NTable,
    BFormInvalidFeedback,
    ValidationProvider,
    ValidationObserver,
    NSearchContainer,
    NSearchInput,
    VSelect,
  },
  data() {
    return {
      items: [],
      loading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        title: this.$route.query.title || null,
        type: this.$route.query.type || null,
        date: this.$route.query.date || null,
      },
      query: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        title: this.$route.query.title || null,
        type: this.$route.query.type || null,
        date: this.$route.query.date || null,
      },
      isModalVisible: false,
      isEditing: false,
      form: {
        merchantUserId: null,
        date: '',
        endDate: '',
        remark: '',
      },
      selectedItem: null,
      submitting: false,
      labels
    }
  },
  computed: {
    modalTitle() {
      return this.isEditing
        ? this.$t('breadcrumb.updateDayOff')
        : this.$t('breadcrumb.createDayOff')
    },
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
    },
    activeSearch() {
      return !!this.$route.query.title || !!this.$route.query.type || !!this.$route.query.date
    },
  },
  watch: {
    perPage(value) {
      this.list(1)
    },
  },
  mounted() {
    this.getData()
  },
  updated() {
    this.$nextTick(() => {
      this.checkRemarkTooltips()
    })
  },
  methods: {
    updateQueryString() {
      this.$router.replace({ query: this.query })
    },
    list(page = 1) {
      this.query.page = page
      this.updateQueryString()
      this.getData()
    },
    sortChanged(sortBy, sortDirection) {
      this.query.order = sortBy
      this.query.sort = sortDirection
      this.updateQueryString()
      this.getData()
    },
    getData() {
      this.loading = true
      this.query.departmentId = this.$route.params.departmentId
      DayOffRepository.list(this.query)
        .then(response => {
          const { list } = response.data.data
          this.items = [...list]
          this.total = response.data.data.total
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    showModal() {
      this.isModalVisible = true
      this.isEditing = false
    },
    editItem(item) {
      this.selectedItem = item
      this.form = {
        merchantUserId: item.merchantUserId.id,
        date: item.date,
        endDate: item.endDate,
        remark: item.remark,
        departmentId: item.departmentId,
      }
      this.isEditing = true
      this.isModalVisible = true
    },
    destroy(id) {
      DayOffRepository.destroy(id)
        .then(() => {
          this.getData()
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    async onSubmit() {
      const isValid = await this.$refs.formValidation.validate()

      if (!isValid || this.submitting) return

      this.submitting = true
      const payload = { ...this.form, departmentId: this.$route.params.departmentId }

      try {
        await (this.isEditing
          ? DayOffRepository.update(this.selectedItem.id, payload)
          : DayOffRepository.create(payload))

        this.getData()
        this.isModalVisible = false
      } catch (error) {
        if (error.response?.status === 422) {
          // Handle validation errors if needed
        }
      } finally {
        this.submitting = false
      }
    },
    resetForm() {
      this.form = {
        merchantUserId: null,
        date: '',
        endDate: '',
        remark: '',
      }
      this.selectedItem = null
      this.$nextTick(() => {
          this.$refs.formValidation?.reset()
      })
    },
    search() {
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQueryString()
      this.getData()
    },
    reset() {
      this.$refs.search.reset()
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQueryString()
      this.getData()
    },
    checkRemarkTooltips() {
      this.$nextTick(() => {
        const remarkCells = document.querySelectorAll('.remark-cell')
        remarkCells.forEach((cell) => {
          const isTruncated = cell.scrollWidth > cell.clientWidth
          if (!isTruncated && cell.id) {
            this.$root.$emit('bv::disable::tooltip', cell.id)
          } else if (isTruncated && cell.id) {
            this.$root.$emit('bv::enable::tooltip', cell.id)
          }
        })
      })
    },
  },
  setup() {
    const permissionType = 'manage-users'
    const route = 'day-off'
    const fields = tableField
    const searchFields = SearchInputs
    return {
      permissionType, route, fields, searchFields,
    }
  },
}
</script>

<style scoped>
::v-deep .table {
  width: 100%;
  table-layout: fixed;
}

::v-deep .table td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
